import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fab,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp,
  Schedule,
  Analytics,
  PostAdd,
  VoiceChat,
  Language,
  VideoLibrary,
  Web,
  Accessibility,
  BrandingWatermark,
  Event,
  Engineering,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getCurrentUser } from '../store/slices/authSlice';
import postsAPI from '../services/postsAPI';
import { Post } from '../types/api';
import AdminTestData from '../components/AdminTestData';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  
  const [stats, setStats] = useState({
    totalPosts: 0,
    scheduled: 0,
    published: 0,
    drafts: 0,
    engagement: 0
  });
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user && isAuthenticated) {
      dispatch(getCurrentUser());
    }

    fetchDashboardData();
  }, [isAuthenticated, user, navigate, dispatch]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch posts data
      const postsResponse = await postsAPI.getPosts({ page: 1 });
      setRecentPosts(postsResponse.data || []);
      
      // Calculate stats from posts
      const posts = postsResponse.data || [];
      const totalPosts = posts.length;
      const scheduled = posts.filter((p: any) => p.status === 'SCHEDULED').length;
      const published = posts.filter((p: any) => p.status === 'PUBLISHED').length;
      const drafts = posts.filter((p: any) => p.status === 'DRAFT').length;
      
      setStats({
        totalPosts,
        scheduled,
        published,
        drafts,
        engagement: Math.floor(Math.random() * 100) // Placeholder until analytics is implemented
      });
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const quickActions = [
    { 
      title: 'Create New Post', 
      icon: AddIcon, 
      color: 'primary', 
      path: '/create-post',
      description: 'AI-powered content generation'
    },
    { 
      title: 'Manage Posts', 
      icon: PostAdd, 
      color: 'secondary', 
      path: '/posts',
      description: 'View, edit, and manage all posts'
    },
    { 
      title: 'Trending Topics', 
      icon: TrendingUp, 
      color: 'success', 
      path: '/trending',
      description: 'Get inspired by current trends'
    },
    { 
      title: 'Schedule Posts', 
      icon: Schedule, 
      color: 'warning', 
      path: '/schedule',
      description: 'Plan your content calendar'
    },
    { 
      title: 'Analytics', 
      icon: Analytics, 
      color: 'info', 
      path: '/analytics',
      description: 'Track performance'
    },
    { 
      title: 'Voice Processing', 
      icon: VoiceChat, 
      color: 'secondary', 
      path: '/voice',
      description: 'Voice to text & audio content'
    },
    { 
      title: 'Website Analysis', 
      icon: Web, 
      color: 'success', 
      path: '/website-analysis',
      description: 'Analyze competitors'
    },
    { 
      title: 'Video Scripts', 
      icon: VideoLibrary, 
      color: 'primary', 
      path: '/video-scripts',
      description: 'Generate video content'
    },
    { 
      title: 'Translation', 
      icon: Language, 
      color: 'info', 
      path: '/translation',
      description: 'Multi-language support'
    }
  ];

  const advancedFeatures = [
    { 
      title: 'Accessibility Tools', 
      icon: Accessibility, 
      path: '/accessibility',
      description: 'Alt-text generation and accessibility checks'
    },
    { 
      title: 'Brand Consistency', 
      icon: BrandingWatermark, 
      path: '/brand',
      description: 'Maintain brand guidelines'
    },
    { 
      title: 'Event Triggers', 
      icon: Event, 
      path: '/events',
      description: 'Automated content for events'
    },
    { 
      title: 'API Testing', 
      icon: Engineering, 
      path: '/api-test',
      description: 'Test all API endpoints'
    }
  ];

  const statCards = [
    { title: 'Total Posts', value: stats.totalPosts, icon: PostAdd, color: 'primary' },
    { title: 'Scheduled', value: stats.scheduled, icon: Schedule, color: 'warning' },
    { title: 'Published', value: stats.published, icon: TrendingUp, color: 'success' },
    { title: 'Drafts', value: stats.drafts, icon: PostAdd, color: 'info' },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1">
                Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ready to create some amazing content?
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label="AI-Powered" color="primary" variant="outlined" />
          <Chip label="Multi-Platform" color="secondary" variant="outlined" />
          <Chip label="Analytics Ready" color="success" variant="outlined" />
          <Chip label="Voice Enabled" color="info" variant="outlined" />
          <Chip label="Brand Safe" color="warning" variant="outlined" />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <IconComponent color={stat.color as any} sx={{ mr: 1, fontSize: 30 }} />
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {loading ? <CircularProgress size={20} /> : stat.value}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer', 
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        }
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <IconComponent 
                          color={action.color as any} 
                          sx={{ fontSize: 40, mb: 1 }} 
                        />
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {action.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Advanced Features */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Advanced Features
            </Typography>
            <Grid container spacing={2}>
              {advancedFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-1px)' }
                      }}
                      onClick={() => navigate(feature.path)}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <IconComponent sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {feature.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Recent Posts
              </Typography>
              {recentPosts.length > 0 && (
                <Button
                  size="small"
                  onClick={() => navigate('/posts')}
                  sx={{ minWidth: 'auto' }}
                >
                  View All
                </Button>
              )}
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : recentPosts.length > 0 ? (
              <>
                <List dense>
                  {recentPosts.slice(0, 5).map((post: any, index) => (
                    <ListItem key={post.id || index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: post.status === 'PUBLISHED' ? 'success.main' : 
                                     post.status === 'SCHEDULED' ? 'warning.main' : 'info.main'
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={post.topic}
                        secondary={`${post.status} • ${new Date(post.created_at).toLocaleDateString()}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/posts')}
                  sx={{ mt: 2 }}
                >
                  Manage All Posts
                </Button>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No posts yet. Start creating!
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/create-post')}
                  size="small"
                >
                  Create Your First Post
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <Tooltip title="Create New Post">
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/create-post')}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      <AdminTestData />
    </Container>
  );
};

export default DashboardPage; 